import React, { useEffect, useMemo, useRef, useState } from "react";
import { logProfileVisit } from "../api/profileVisitsApi";

const PUBLIC_PROFILE_API_BASE =
  window?.pfandpiratData?.publicProfileApiBaseUrl ||
  "/wp-json/pfandpirat/v1/public-profile/";

const PUBLIC_PROFILE_SITE_BASE = "https://pfandpirat.com/piraten/";
const PUBLIC_PROFILE_ACTIVITY_SUFFIX = '/activity';
const PUBLIC_PROFILE_APP_BASE = "https://app.pfandpirat.com/app/#/profil/";

function buildNoCacheUrl(url) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_pfp_ts=${Date.now()}`;
}

function buildPublicProfileUrl(profile) {
  if (profile?.public_url) return profile.public_url;
  const slug = profile?.slug || profile?.username || "";
  return slug ? `${PUBLIC_PROFILE_SITE_BASE}${encodeURIComponent(slug)}` : "";
}

function buildAppProfileUrl(profile) {
  if (profile?.app_url) return profile.app_url;
  const slug = profile?.slug || profile?.username || "";
  return slug ? `${PUBLIC_PROFILE_APP_BASE}${encodeURIComponent(slug)}` : "";
}


function buildPublicProfileActivityUrl(username) {
  const slug = String(username || '').trim();
  if (!slug) return '';
  return `${PUBLIC_PROFILE_API_BASE}${encodeURIComponent(slug)}${PUBLIC_PROFILE_ACTIVITY_SUFFIX}`;
}

function getLevelDisplay(profile) {
  const lvl = Number(profile?.level || 0);
  const rankTitle = profile?.rank?.title || "Matrose";
  const rankIcon = profile?.rank?.icon || "🧭";
  return `${rankIcon} ${rankTitle} (Level ${lvl})`;
}

function normalizeAchievementItems(profile) {
  if (!profile) return [];

  const source = Array.isArray(profile.achievement_items) && profile.achievement_items.length > 0
    ? profile.achievement_items
    : Array.isArray(profile.achievements)
      ? profile.achievements
      : [];

  return source
    .map((item) => {
      if (typeof item === "string") {
        return { key: item, title: item, badge_icon: "🏅" };
      }
      if (!item?.title) return null;
      return {
        key: item.key || item.title,
        title: item.title,
        badge_icon: item.badge_icon || "🏅",
      };
    })
    .filter(Boolean);
}


function normalizeProfileResponse(data, fallbackUsername = "") {
  const raw = data && typeof data === "object" && data.profile && typeof data.profile === "object"
    ? data.profile
    : data;

  if (!raw || typeof raw !== "object") {
    return null;
  }

  const slug = String(raw.slug || raw.username || fallbackUsername || "").trim();
  const username = String(raw.username || raw.slug || fallbackUsername || "").trim();
  const displayName = String(raw.display_name || raw.displayName || username || slug || "").trim();
  const avatar = String(raw.avatar || raw.avatar_url || raw.image_url || "").trim();
  const avatarFallback = String(raw.avatar_fallback || raw.avatar_fallback_url || avatar || "").trim();
  const bio = String(raw.bio || "").trim();
  const reason = String(raw.reason || "").trim();

  return {
    ...raw,
    slug,
    username,
    display_name: displayName,
    avatar,
    avatar_fallback: avatarFallback,
    bio,
    reason,
  };
}

function StatCard({ label, value, subvalue = "", accent = "#0c3563", bg = "#ffffff", border = "#dfe7ef" }) {
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 18,
        padding: "1rem",
        minHeight: 96,
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          fontSize: "0.88rem",
          color: "#65707d",
          marginBottom: "0.45rem",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.5rem",
          lineHeight: 1.2,
          color: accent,
          fontWeight: 800,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
      {subvalue ? (
        <div
          style={{
            marginTop: "0.55rem",
            color: "#65707d",
            fontSize: "0.92rem",
            lineHeight: 1.45,
          }}
        >
          {subvalue}
        </div>
      ) : null}
    </div>
  );
}

function ContentCard({ title, children, icon = "" }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e3e9f0",
        borderRadius: 20,
        padding: "1.1rem",
        boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          fontSize: "1.05rem",
          color: "#0c3563",
          fontWeight: 800,
          marginBottom: "0.75rem",
          display: "flex",
          alignItems: "center",
          gap: "0.45rem",
        }}
      >
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div
        style={{
          color: "#4f5d6c",
          lineHeight: 1.6,
          fontSize: "0.98rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function PublicProfile({ username }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [avatarSrc, setAvatarSrc] = useState('');
  const visitLogRef = useRef('');
  const [activityItems, setActivityItems] = useState([]);
  const [activityCursor, setActivityCursor] = useState('');
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      if (!username) {
        if (isMounted) {
          setLoading(false);
          setErrorCode("missing_username");
          setErrorMessage("Kein Benutzername für das öffentliche Profil übergeben.");
        }
        return;
      }

      setLoading(true);
      setErrorCode("");
      setErrorMessage("");

      try {
        const url = buildNoCacheUrl(`${PUBLIC_PROFILE_API_BASE}${encodeURIComponent(username)}`);
        const response = await fetch(url, {
          credentials: "include",
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (!isMounted) return;

          setProfile(null);
          setErrorCode(data?.code || "profile_error");
          setErrorMessage(
            data?.message || "Das öffentliche Profil konnte nicht geladen werden."
          );
          setLoading(false);
          return;
        }

        if (!isMounted) return;

        const normalized = normalizeProfileResponse(data, username);
        if (!normalized) {
          setProfile(null);
          setErrorCode("profile_error");
          setErrorMessage("Das öffentliche Profil wurde in einem unerwarteten Format geliefert.");
          setLoading(false);
          return;
        }

        setProfile(normalized);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;

        setProfile(null);
        setErrorCode("network_error");
        setErrorMessage("Netzwerkfehler beim Laden des öffentlichen Profils.");
        setLoading(false);
      }
    }

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [username]);

  const achievements = useMemo(() => normalizeAchievementItems(profile), [profile]);

  useEffect(() => {
    const initialItems = Array.isArray(profile?.activity) ? profile.activity : [];
    setActivityItems(initialItems);
    setActivityCursor(String(profile?.activity_meta?.next_cursor || ''));
    setActivityHasMore(!!profile?.activity_meta?.has_more);
  }, [profile?.activity, profile?.activity_meta?.next_cursor, profile?.activity_meta?.has_more]);

  async function loadMoreActivity() {
    const slug = String(profile?.slug || profile?.username || '').trim();
    if (!slug || !activityHasMore || activityLoadingMore) return;

    const baseUrl = buildPublicProfileActivityUrl(slug);
    if (!baseUrl) return;

    setActivityLoadingMore(true);
    try {
      const url = buildNoCacheUrl(`${baseUrl}?limit=10${activityCursor ? `&cursor=${encodeURIComponent(activityCursor)}` : ''}`);
      const response = await fetch(url, {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return;
      }

      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setActivityItems((prev) => {
        const seen = new Set(prev.map((item) => String(item?.id || item?.cursor || '')));
        const merged = [...prev];
        nextItems.forEach((item) => {
          const key = String(item?.id || item?.cursor || '');
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        });
        return merged;
      });
      setActivityCursor(String(data?.next_cursor || ''));
      setActivityHasMore(!!data?.has_more);
    } finally {
      setActivityLoadingMore(false);
    }
  }

  useEffect(() => {
    const primary = String(profile?.avatar || '').trim();
    const fallback = String(profile?.avatar_fallback || '').trim();
    const safe = 'https://app.pfandpirat.com/wp-content/uploads/Pfandpirat-7.jpg';
    setAvatarSrc(primary || fallback || safe);
  }, [profile?.avatar, profile?.avatar_fallback]);

  useEffect(() => {
    const slug = String(profile?.slug || '').trim();
    const username = String(profile?.username || '').trim();
    if (!slug && !username) return;

    const key = (slug || username).toLowerCase();
    if (visitLogRef.current === key) return;
    visitLogRef.current = key;

    logProfileVisit(slug || username, 'public_profile').catch(() => {});
  }, [profile?.slug, profile?.username]);

  const visibleStats = useMemo(() => {
    if (!profile) return [];

    const cards = [];
    const showLevel = profile?.stats?.show_level ?? (typeof profile.level !== "undefined");
    const showXp = profile?.stats?.show_xp ?? (typeof profile.xp !== "undefined");
    const showAchievements = profile?.stats?.show_achievements ?? (typeof profile.achievement_count !== "undefined" || achievements.length > 0);
    const showLastActivity = profile?.stats?.show_last_activity ?? !!profile.last_activity;

    if (showLevel && typeof profile.level !== "undefined") {
      const progressText = profile?.progress
        ? `${profile.progress.progress_in_level} / ${profile.progress.progress_needed} XP in diesem Level · noch ${profile.progress.xp_to_next_level} XP`
        : "";

      cards.push({
        key: "level",
        label: "Rang & Level",
        value: getLevelDisplay(profile),
        subvalue: progressText,
        accent: "#0c3563",
        bg: "#eef6ff",
        border: "#cfe3fb",
      });
    }

    if (showXp && typeof profile.xp !== "undefined") {
      cards.push({
        key: "xp",
        label: "XP",
        value: Number(profile.xp || 0).toLocaleString("de-DE"),
        accent: "#ff9800",
        bg: "#fff7e8",
        border: "#f3d59a",
      });
    }

    if (showAchievements) {
      cards.push({
        key: "achievements",
        label: "Erfolge",
        value: profile.achievement_count ?? achievements.length ?? 0,
        accent: "#198754",
        bg: "#eefbf3",
        border: "#cdeed9",
      });
    }

    if (showLastActivity && profile.last_activity) {
      cards.push({
        key: "activity",
        label: "Aktivität",
        value: profile.last_activity,
        accent: "#6b7280",
        bg: "#f6f8fb",
        border: "#dde5ee",
      });
    }

    return cards;
  }, [profile, achievements]);

  const hasProfileText = !!(profile?.bio || "").trim();
  const hasReason = !!(profile?.reason || "").trim();
  const hasCity = !!(profile?.city || "").trim();
  const hasAgeRange = !!(profile?.age_range || "").trim();
  const showActivityFeed = profile?.stats?.show_activity_feed ?? activityItems.length > 0;

  const publicUrl = useMemo(() => buildPublicProfileUrl(profile), [profile]);
  const appUrl = useMemo(() => buildAppProfileUrl(profile), [profile]);

  if (loading) {
    return (
      <div style={{ maxWidth: 980, margin: "2rem auto", padding: "1rem" }}>
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e3e9f0",
            borderRadius: 20,
            padding: "1.4rem",
            color: "#4f5d6c",
            textAlign: "center",
          }}
        >
          Lade öffentliches Profil ...
        </div>
      </div>
    );
  }

  if (!profile) {
    const isHiddenProfile =
      errorCode === "pfp_public_profile_disabled" ||
      errorCode === "rest_no_route" ||
      errorMessage.includes("nicht öffentlich verfügbar");

    return (
      <div style={{ maxWidth: 980, margin: "2rem auto", padding: "1rem" }}>
        <div
          style={{
            background: "linear-gradient(180deg, #fffdf8 0%, #f7fbff 100%)",
            border: "1px solid #e3e9f0",
            borderRadius: 20,
            padding: "1.5rem",
            boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              display: "inline-block",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#9a6200",
              background: "#fff3d6",
              borderRadius: 999,
              padding: "0.28rem 0.7rem",
              marginBottom: "0.8rem",
            }}
          >
            Öffentliches Piratenprofil
          </div>

          <h2 style={{ margin: "0 0 0.5rem 0", color: "#0c3563", fontSize: "1.5rem" }}>
            {isHiddenProfile ? "Dieses Profil ist nicht öffentlich sichtbar." : "Profil konnte nicht geladen werden."}
          </h2>

          <p style={{ margin: 0, color: "#5e6b78", lineHeight: 1.6, fontSize: "1rem" }}>
            {isHiddenProfile
              ? "Der Nutzer hat sein öffentliches Profil aktuell nicht freigeschaltet oder die Seite ist nicht öffentlich verfügbar."
              : errorMessage || "Beim Laden des Profils ist ein Fehler aufgetreten."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile" style={{ maxWidth: 980, margin: "1.5rem auto 2rem", padding: "0 1rem" }}>
      <div
        style={{
          background: "linear-gradient(180deg, #fffdf8 0%, #f7fbff 100%)",
          border: "2px solid #f0c96b",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            padding: "1.4rem",
            background: "linear-gradient(135deg, rgba(255,243,214,0.95) 0%, rgba(247,251,255,0.95) 100%)",
            borderBottom: "1px solid #e4e9ef",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "1.25rem",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: "1rem", alignItems: "center", minWidth: 0, flex: "1 1 420px" }}>
              <img
                src={avatarSrc || profile.avatar_fallback || profile.avatar || ''}
                alt={`${profile.display_name || profile.username || "Profil"} Avatar`}
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "4px solid #fff",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                  background: "#fff",
                  flexShrink: 0,
                }}
                onError={() => {
                  const fallback = String(profile?.avatar_fallback || '').trim();
                  const primary = String(profile?.avatar || '').trim();
                  const current = String(avatarSrc || '').trim();

                  if (fallback && current !== fallback) {
                    setAvatarSrc(fallback);
                    return;
                  }

                  if (primary && fallback && primary !== fallback && current !== primary) {
                    setAvatarSrc(primary);
                    return;
                  }

                  setAvatarSrc('https://app.pfandpirat.com/wp-content/uploads/Pfandpirat-7.jpg');
                }}
              />

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "inline-block",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "#9a6200",
                    background: "#fff3d6",
                    borderRadius: 999,
                    padding: "0.28rem 0.7rem",
                    marginBottom: "0.6rem",
                  }}
                >
                  Öffentliches Piratenprofil
                </div>

                <h1
                  style={{
                    margin: "0 0 0.2rem 0",
                    color: "#0c3563",
                    fontSize: "1.9rem",
                    lineHeight: 1.15,
                    wordBreak: "break-word",
                  }}
                >
                  {profile.display_name || profile.username}
                </h1>

                <div
                  style={{
                    color: "#607080",
                    fontSize: "1rem",
                    fontWeight: 700,
                    marginBottom: "0.65rem",
                    wordBreak: "break-word",
                  }}
                >
                  @{profile.slug || profile.username}
                </div>

                <div style={{ color: "#5c6978", fontSize: "1rem", marginBottom: "0.65rem" }}>
                  {(profile.display_name || profile.username)}{hasCity ? ` – aus ${profile.city}` : ''}{profile?.rank?.title ? ` – ${profile.rank.title} auf Level ${profile.level}` : ''}
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {hasCity && (
                    <span style={{
                      background: "#ffffff",
                      border: "1px solid #dbe4ee",
                      color: "#0c3563",
                      borderRadius: 999,
                      padding: "0.42rem 0.75rem",
                      fontSize: "0.9rem",
                      fontWeight: 700,
                    }}>
                      📍 {profile.city}
                    </span>
                  )}

                  {hasAgeRange && (
                    <span style={{
                      background: "#ffffff",
                      border: "1px solid #dbe4ee",
                      color: "#0c3563",
                      borderRadius: 999,
                      padding: "0.42rem 0.75rem",
                      fontSize: "0.9rem",
                      fontWeight: 700,
                    }}>
                      👤 {profile.age_range}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ flex: "0 0 auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "0.9rem 1rem", minWidth: 250, maxWidth: "100%" }}>
              {publicUrl ? (
                <>
                  <div style={{ fontSize: "0.82rem", color: "#65707d", marginBottom: "0.35rem" }}>
                    Öffentliche Profilseite
                  </div>
                  <a
                    href={publicUrl}
                    style={{
                      color: "#1e88e5",
                      textDecoration: "none",
                      fontWeight: 700,
                      wordBreak: "break-all",
                      fontSize: "0.92rem",
                      display: "block",
                      marginBottom: appUrl ? "0.85rem" : 0,
                    }}
                  >
                    {publicUrl}
                  </a>
                </>
              ) : null}

              {appUrl ? (
                <>
                  <div style={{ fontSize: "0.82rem", color: "#65707d", marginBottom: "0.35rem" }}>
                    Profil in der App
                  </div>
                  <a
                    href={appUrl}
                    style={{
                      display: "inline-block",
                      background: "#1e88e5",
                      color: "#fff",
                      textDecoration: "none",
                      fontWeight: 700,
                      fontSize: "0.92rem",
                      padding: "0.7rem 1rem",
                      borderRadius: 12,
                    }}
                  >
                    In der App öffnen
                  </a>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ padding: "1.2rem" }}>
          {visibleStats.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              {visibleStats.map((item) => (
                <StatCard
                  key={item.key}
                  label={item.label}
                  value={item.value}
                  subvalue={item.subvalue}
                  accent={item.accent}
                  bg={item.bg}
                  border={item.border}
                />
              ))}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1rem",
            }}
          >
            {hasProfileText && (
              <ContentCard title="Über mich" icon="⚓">
                {profile.bio}
              </ContentCard>
            )}

            {hasReason && (
              <ContentCard title="Warum ich Pfandpirat nutze" icon="🏴‍☠️">
                {profile.reason}
              </ContentCard>
            )}
          </div>

          {(profile?.stats?.show_achievements ?? (achievements.length > 0 || typeof profile?.achievement_count !== "undefined")) && achievements.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <ContentCard title="Freigeschaltete Erfolge" icon="🎖️">
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.55rem" }}>
                  {achievements.map((achievement, index) => (
                    <span
                      key={`${achievement.key || achievement.title}-${index}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        padding: "0.55rem 0.8rem",
                        background: "#fff7e8",
                        border: "1px solid #f3d59a",
                        borderRadius: 999,
                        color: "#8d5b00",
                        fontSize: "0.92rem",
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      {achievement.badge_icon || '🏅'} {achievement.title}
                    </span>
                  ))}
                </div>
              </ContentCard>
            </div>
          )}


          {showActivityFeed && (
            <div style={{ marginTop: '1rem' }}>
              <ContentCard title="Letzte Aktivität" icon="📜">
                {activityItems.length > 0 ? (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {activityItems.map((item, index) => (
                      <div
                        key={`${item.id || item.cursor || item.created_at}-${index}`}
                        style={{
                          background: '#fffaf0',
                          border: '1px solid #f0ddab',
                          borderRadius: 16,
                          padding: '0.9rem 1rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: '#0c3563', fontWeight: 800, fontSize: '0.98rem', marginBottom: '0.25rem' }}>
                              <span style={{ marginRight: '0.45rem' }}>{item.icon || '📝'}</span>
                              {item.title || 'Aktivität'}
                            </div>
                            <div style={{ color: '#4f5d6c', fontSize: '0.95rem', lineHeight: 1.55 }}>
                              {item.summary}
                            </div>
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt=""
                                loading="lazy"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  maxWidth: 360,
                                  aspectRatio: '4 / 3',
                                  objectFit: 'cover',
                                  borderRadius: 12,
                                  marginTop: '0.75rem',
                                  border: '1px solid #ead8a4',
                                  background: '#f8fafc',
                                }}
                              />
                            ) : null}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.65rem' }}>
                              {Number(item?.xp_awarded || 0) !== 0 ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    padding: '0.35rem 0.6rem',
                                    background: Number(item?.xp_awarded || 0) < 0 ? '#fff1f2' : '#eefbf2',
                                    border: `1px solid ${Number(item?.xp_awarded || 0) < 0 ? '#fecdd3' : '#c7ebd1'}`,
                                    borderRadius: 999,
                                    color: Number(item?.xp_awarded || 0) < 0 ? '#b42318' : '#166534',
                                    fontSize: '0.82rem',
                                    fontWeight: 700,
                                  }}
                                >
                                  {Number(item?.xp_awarded || 0) < 0 ? '⚠️' : '✨'} {Number(item.xp_awarded) > 0 ? '+' : ''}{Number(item.xp_awarded)} XP
                                </span>
                              ) : null}
                              {item.drink_url ? (
                                <a
                                  href={item.drink_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    padding: '0.35rem 0.6rem',
                                    background: '#eef6ff',
                                    border: '1px solid #cfe0f5',
                                    borderRadius: 999,
                                    color: '#1e5eb8',
                                    fontSize: '0.82rem',
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                  }}
                                >
                                  🔗 {item.drink_title ? 'Getränkeseite öffnen' : 'Getränk ansehen'}
                                </a>
                              ) : null}
                            </div>
                          </div>
                          <div style={{ color: '#7b8794', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {item.relative_time || ''}
                          </div>
                        </div>
                      </div>
                    ))}

                    {activityHasMore && (
                      <div>
                        <button
                          type="button"
                          onClick={loadMoreActivity}
                          disabled={activityLoadingMore}
                          style={{
                            border: '1px solid #cfd8e3',
                            background: activityLoadingMore ? '#eef2f7' : '#ffffff',
                            color: '#0c3563',
                            borderRadius: 12,
                            padding: '0.75rem 1rem',
                            fontWeight: 700,
                            cursor: activityLoadingMore ? 'default' : 'pointer',
                          }}
                        >
                          {activityLoadingMore ? 'Lade weitere Aktivität ...' : 'Mehr Aktivität laden'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#65707d' }}>Noch keine öffentliche Aktivität vorhanden.</div>
                )}
              </ContentCard>
            </div>
          )}

          <div
            style={{
              marginTop: "1rem",
              background: "#fff6e7",
              border: "1px solid #f0cf84",
              borderRadius: 16,
              padding: "0.95rem 1rem",
              color: "#805300",
              fontSize: "0.95rem",
              lineHeight: 1.55,
            }}
          >
            Dieses öffentliche Profil zeigt bewusst nur Community-relevante Angaben. Geldsummen, Auszahlungen und private Details bleiben weiterhin verborgen.
          </div>
        </div>
      </div>
    </div>
  );
}
